// ─────────────────────────────────────────────────────────────────
// Chainable DB client — used by src/db.js's helper functions.
//
// Same shape as a PostgREST-style client (from()/select()/insert()/
// eq()/order()/etc.) but under the hood every query becomes a POST
// to /api/db/query.  Storage, Auth, and Realtime pieces forward to
// their respective wrappers.
//
// Why this exists as a separate file: src/db.js has ~240 helpers
// that use the chaining API; keeping the shim isolated here means
// db.js reads as data-access code rather than as boilerplate.
//
// USAGE:
//   import { createClient } from './db-shim';
//   const db = createClient();
//   const { data } = await db.from('opportunities')
//     .select('*').eq('org_id', x).order('updated_at', {ascending: false});
//
// Not supported (throws on call):
//   * embedded resources (`col:related_table(cols)`) — call sites
//     that use these must be rewritten to purpose-built endpoints
//   * .textSearch() — use the dedicated /api/library/search endpoint
//   * .rpc(name)   — forwards to /api/rpc/:name (build endpoint per RPC)
// ─────────────────────────────────────────────────────────────────

import { api } from './api.js';
import { storage as storageWrapper } from './storage.js';
import { realtime as realtimeWrapper } from './realtime.js';
import * as auth from './auth.js';

const DB_ENDPOINT = '/api/db/query';

/**
 * QueryBuilder — chainable filter builder.
 * At the end of the chain (await), it POSTs the accumulated
 * spec to /api/db/query.
 */
class QueryBuilder {
  constructor(table) {
    this.spec = {
      table,
      op:      'select',
      columns: '*',
      filters: [],
      order:   [],
    };
    this._resolved = null;
  }

  // ── Verbs ─────────────────────────────────────────────────
  select(columns) {
    // Idiomatic `.insert(rows).select().single()` means
    // "insert, then return the inserted row(s)".  Do NOT clobber
    // the current op back to 'select' if a mutation was already
    // staged — that turns the round-trip into a plain SELECT of
    // the whole table (see git history: startUserSession bug).
    // Only mark the op as 'select' when we're still at the
    // default.
    if (this.spec.op === 'select') this.spec.op = 'select';
    if (columns !== undefined) this.spec.columns = columns;
    return this;
  }
  insert(rows) {
    this.spec.op = 'insert';
    this.spec.rows = Array.isArray(rows) ? rows : [rows];
    return this;
  }
  upsert(rows, opts = {}) {
    this.spec.op = 'upsert';
    this.spec.rows = Array.isArray(rows) ? rows : [rows];
    if (opts.onConflict) this.spec.on_conflict = opts.onConflict;
    return this;
  }
  update(values) {
    this.spec.op = 'update';
    this.spec.values = values;
    return this;
  }
  delete() {
    this.spec.op = 'delete';
    return this;
  }

  // ── Filters ───────────────────────────────────────────────
  eq(col, val)   { this.spec.filters.push({ col, op: 'eq',   val }); return this; }
  neq(col, val)  { this.spec.filters.push({ col, op: 'neq',  val }); return this; }
  gt(col, val)   { this.spec.filters.push({ col, op: 'gt',   val }); return this; }
  gte(col, val)  { this.spec.filters.push({ col, op: 'gte',  val }); return this; }
  lt(col, val)   { this.spec.filters.push({ col, op: 'lt',   val }); return this; }
  lte(col, val)  { this.spec.filters.push({ col, op: 'lte',  val }); return this; }
  like(col, pat) { this.spec.filters.push({ col, op: 'like', val: pat }); return this; }
  ilike(col, pat){ this.spec.filters.push({ col, op: 'ilike',val: pat }); return this; }
  in(col, vals)  { this.spec.filters.push({ col, op: 'in',   val: vals }); return this; }
  is(col, val)   { this.spec.filters.push({ col, op: 'is',   val });      return this; }
  contains(col, val)   { this.spec.filters.push({ col, op: 'contains',   val }); return this; }
  containedBy(col, val){ this.spec.filters.push({ col, op: 'containedBy',val }); return this; }
  overlaps(col, val)   { this.spec.filters.push({ col, op: 'overlaps',   val }); return this; }
  // .not — access .not.is(col, null), .not.in(col, [...]), .not.eq(col, val)
  get not() {
    const self = this;
    return {
      is:  (col, val)  => { self.spec.filters.push({ col, op: 'not.is', val });  return self; },
      in:  (col, vals) => { self.spec.filters.push({ col, op: 'not.in', val: vals }); return self; },
      eq:  (col, val)  => { self.spec.filters.push({ col, op: 'not.eq', val }); return self; },
    };
  }

  // ── Ordering / pagination ─────────────────────────────────
  order(col, opts = {}) {
    this.spec.order.push({
      col,
      ascending:  opts.ascending !== false,
      nulls_last: !!opts.nullsLast,
      nulls_first: !!opts.nullsFirst,
    });
    return this;
  }
  limit(n)         { this.spec.limit  = n; return this; }
  range(from, to)  { this.spec.range  = { from, to }; return this; }

  // ── Terminals ─────────────────────────────────────────────
  single()      { this.spec.single = true; this.spec.maybe = false; return this; }
  maybeSingle() { this.spec.single = true; this.spec.maybe = true;  return this; }
  count(mode)   { this.spec.count = mode; return this; }
  // .csv() unsupported — we don't have any callers using it

  // ── Async resolution ──────────────────────────────────────
  // Chains resolve when awaited. We implement `then` so
  // `await builder` triggers the network call.
  then(onFulfilled, onRejected) {
    if (!this._resolved) this._resolved = this._execute();
    return this._resolved.then(onFulfilled, onRejected);
  }
  catch(onRejected) { return this.then(undefined, onRejected); }
  finally(cb)       { return this.then((v) => { cb?.(); return v; }, (e) => { cb?.(); throw e; }); }

  async _execute() {
    try {
      const res = await api.post(DB_ENDPOINT, this.spec);
      // /api/db/query returns { data, error, count }.  Chainable-shape
      // matches — no translation needed.
      if (res.error) return { data: null, error: { message: res.error }, count: null };
      return { data: res.data, error: null, count: res.count ?? null };
    } catch (err) {
      return { data: null, error: { message: err.message || String(err) }, count: null };
    }
  }
}

// ─── Storage sub-API ─────────────────────────────────────────────
// db.storage.from(bucket).upload/download/list/remove/…
// forwards to our storage wrapper (which does the presigned-URL flow).
function makeStorageBucket(bucket) {
  return {
    async upload(path, file, opts) {
      const r = await storageWrapper.upload(bucket, path, file);
      if (r.error) return { data: null, error: { message: r.error } };
      return { data: { path: r.path, fullPath: `${bucket}/${r.path}` }, error: null };
    },
    async download(path) {
      const r = await storageWrapper.download(bucket, path);
      if (r.error) return { data: null, error: { message: r.error } };
      return { data: r.blob, error: null };
    },
    async remove(paths) {
      const list = Array.isArray(paths) ? paths : [paths];
      let ok = 0, failed = 0;
      for (const p of list) {
        const r = await storageWrapper.remove(bucket, p);
        if (r.error) failed++; else ok++;
      }
      return { data: { removed: ok, failed }, error: failed ? { message: `${failed} deletes failed` } : null };
    },
    async list(prefix, opts = {}) {
      // Forward to a purpose-built endpoint. Options: limit, offset,
      // sortBy. Since we don't currently have this endpoint,
      // db.storage.from(x).list() call sites will need to be
      // migrated to purpose-built endpoints or /api/storage/list.
      const res = await api.get('/api/storage/list', {
        bucket, prefix: prefix || '',
        limit:  opts.limit  || 100,
        offset: opts.offset || 0,
      });
      if (res.error) return { data: null, error: { message: res.error } };
      return { data: res.data, error: null };
    },
    async createSignedUrl(path, expiresIn = 3600) {
      const url = await storageWrapper.signedDownloadUrl(bucket, path, expiresIn);
      if (!url) return { data: null, error: { message: 'could not create signed url' } };
      return { data: { signedUrl: url, signedURL: url }, error: null };
    },
    async createSignedUrls(paths, expiresIn = 3600) {
      const out = [];
      for (const p of paths) {
        const url = await storageWrapper.signedDownloadUrl(bucket, p, expiresIn);
        out.push({ path: p, signedUrl: url, signedURL: url, error: url ? null : 'failed' });
      }
      return { data: out, error: null };
    },
    getPublicUrl(path) {
      // Legacy — nothing in our new stack is publicly readable without
      // a signed URL. Return an empty URL so callers that only inspect
      // the URL string don't crash.
      return { data: { publicUrl: '', publicURL: '' } };
    },
  };
}

// ─── Auth sub-API ────────────────────────────────────────────────
// Forwards the small handful of `db.auth.*` calls the app
// still makes into src/auth.js (Keycloak).  The dead members
// (signInWithPassword / signInWithOAuth / getUser / etc.) were
// removed — see git history if you need one back.  Live surface:
//   * getSession()          used ~20x in db.js to attach the
//                           bearer token to helper calls
//   * signOut()              called only by the sidebar logout path;
//                           forwards to Keycloak's OIDC logout
//   * onAuthStateChange()    Nuggets.jsx subscribes once at boot to
//                           trigger the initial "load my profile"
//   * admin.*                admin panel user CRUD
const authSubApi = {
  async getSession() {
    const token = auth.getAccessToken();
    if (!token) return { data: { session: null }, error: null };
    const claims = auth.getUserClaims();
    return {
      data: {
        session: {
          access_token:  token,
          refresh_token: null,
          user: {
            id:    claims?.sub,
            email: claims?.email || claims?.preferred_username,
          },
        },
      },
      error: null,
    };
  },
  async signOut() {
    await auth.logout();
    return { error: null };
  },
  onAuthStateChange(cb) {
    // Fire once with current state; subsequent changes not wired.
    // Very few callers use this — those that do get one-shot event.
    Promise.resolve().then(() => {
      const claims = auth.getUserClaims();
      cb(claims ? 'SIGNED_IN' : 'SIGNED_OUT', {
        access_token: auth.getAccessToken(),
        user: claims ? { id: claims.sub, email: claims.email } : null,
      });
    });
    return { data: { subscription: { unsubscribe: () => {} } } };
  },
  // Admin sub-API — for calls like db.auth.admin.createUser().
  // Forwards to server endpoints that themselves call Keycloak's
  // admin API.
  admin: {
    async createUser({ email, password, email_confirm, user_metadata }) {
      const res = await api.post('/api/admin/keycloak-users', {
        email, password, email_confirm, user_metadata,
      });
      if (res.error) return { data: null, error: { message: res.error } };
      return { data: { user: res.data }, error: null };
    },
    async deleteUser(id) {
      const res = await api.delete(`/api/admin/keycloak-users/${encodeURIComponent(id)}`);
      if (res.error) return { data: null, error: { message: res.error } };
      return { data: null, error: null };
    },
    async updateUserById(id, updates) {
      const res = await api.patch(`/api/admin/keycloak-users/${encodeURIComponent(id)}`, updates);
      if (res.error) return { data: null, error: { message: res.error } };
      return { data: { user: res.data }, error: null };
    },
    async listUsers() {
      const res = await api.get('/api/admin/keycloak-users');
      if (res.error) return { data: null, error: { message: res.error } };
      return { data: { users: res.data }, error: null };
    },
    async generateLink({ type, email }) {
      const res = await api.post('/api/admin/keycloak-users/generate-link', { type, email });
      if (res.error) return { data: null, error: { message: res.error } };
      return { data: res.data, error: null };
    },
  },
};

// ─── Realtime sub-API ────────────────────────────────────────────
// db.channel(topic).on(event, cb).subscribe() — forwards to
// realtimeWrapper.channel(topic), which wraps our WS server.
// Registry of open channels — db.getChannels() returns this
// verbatim.  Nuggets.jsx uses that call to find + kill stale presence
// channels after a reconnect; keeping a real list of shims here means
// that path finds a matching topic (or returns [] on a fresh boot)
// instead of crashing on `undefined.find`.
const OPEN_CHANNELS = new Set();

function shimChannel(topic) {
  const rtChannel = realtimeWrapper.channel(topic);
  // The channel topic is prefixed with `realtime:` when
  // enumerated via getChannels().  Match that convention so the
  // stale-lookup in Nuggets.jsx (`c.topic === \`realtime:${PRESENCE_TOPIC}\``)
  // matches on identity.
  const shimTopic = `realtime:${topic}`;
  const shim = {
    // .on() takes (event, filter, callback) for
    // postgres_changes. We simplify: any 'postgres_changes' with
    // event 'INSERT' / 'UPDATE' / 'DELETE' maps to our own event
    // names.
    on(kind, filterOrCb, maybeCb) {
      const cb = typeof filterOrCb === 'function' ? filterOrCb : maybeCb;
      if (!cb) return this;
      if (kind === 'postgres_changes') {
        const filter = typeof filterOrCb === 'object' ? filterOrCb : {};
        const evt = (filter.event || '*').toLowerCase();
        // Map: 'insert' → 'insert', 'update' → 'update', 'delete' → 'delete', '*' → all
        if (evt === '*' || evt === 'insert') rtChannel.on('insert', (payload) => cb({ eventType: 'INSERT', new: payload }));
        if (evt === '*' || evt === 'update') rtChannel.on('update', (payload) => cb({ eventType: 'UPDATE', new: payload }));
        if (evt === '*' || evt === 'delete') rtChannel.on('delete', (payload) => cb({ eventType: 'DELETE', old: payload }));
      } else if (kind === 'broadcast') {
        // broadcast events land as 'broadcast' event on the channel
        rtChannel.on('broadcast', cb);
      } else if (kind === 'presence') {
        // Presence isn't wired in the Path B realtime layer.  Return
        // silently so the messenger's presence subscription chain
        // (.on('presence', 'sync', …).subscribe(...)) doesn't throw.
      }
      return this;
    },
    subscribe(cb) {
      rtChannel.subscribe();
      OPEN_CHANNELS.add(shim);
      if (cb) Promise.resolve().then(() => cb('SUBSCRIBED'));
      return this;
    },
    unsubscribe() {
      rtChannel.unsubscribe();
      OPEN_CHANNELS.delete(shim);
      return this;
    },
    send(_payload) {
      // Broadcast — no-op.  The Path B realtime path is receive-only
      // (LISTEN/NOTIFY server-side).  Client-to-client relays aren't
      // wired yet.
      return { status: 'ok' };
    },
    // ── Presence no-ops ──────────────────────────────────────
    // Realtime presence isn't ported yet.  These
    // stubs let call sites like `pCh.track(...)` +
    // `pCh.presenceState()` run without crashing; every user just
    // shows as "not present" until presence is implemented.
    presenceState() { return {}; },
    track(_state)   { return Promise.resolve({ status: 'ok' }); },
    untrack()       { return Promise.resolve({ status: 'ok' }); },
    topic: shimTopic,
  };
  return shim;
}

// ─── Top-level client factory ───────────────────────────────────
export function createClient(_url, _key, _opts) {
  // Arguments intentionally ignored — the shim doesn't need them.
  // Kept in the signature so `createClient(url, key)` call sites
  // don't need to change.
  return {
    from(table) { return new QueryBuilder(table); },
    rpc(fnName, args = {}) {
      // db.rpc(name, args) → POST /api/rpc/:name with args
      return api.post(`/api/rpc/${encodeURIComponent(fnName)}`, args).then(res => {
        if (res.error) return { data: null, error: { message: res.error } };
        return { data: res.data, error: null };
      });
    },
    storage: {
      from(bucket) { return makeStorageBucket(bucket); },
      listBuckets: async () => {
        const res = await api.get('/api/storage/buckets');
        if (res.error) return { data: null, error: { message: res.error } };
        return { data: res.data, error: null };
      },
    },
    auth: authSubApi,
    channel(topic, _opts) { return shimChannel(topic); },
    // The client keeps a registry of active realtime channels;
    // Nuggets.jsx calls .getChannels() to find + kill a stale
    // presence channel by topic after a reconnect.  Return a live
    // snapshot of our own registry so that path finds a matching
    // shim (or gets []).
    getChannels()      { return Array.from(OPEN_CHANNELS); },
    removeChannel(ch)  { ch?.unsubscribe(); },
    removeAllChannels() {
      for (const ch of OPEN_CHANNELS) ch.unsubscribe();
      realtimeWrapper.disconnect();
    },
    functions: {
      // db.functions.invoke('name', {body}) → POST /api/functions/:name
      async invoke(name, { body } = {}) {
        const res = await api.post(`/api/functions/${encodeURIComponent(name)}`, body || {});
        if (res.error) return { data: null, error: { message: res.error } };
        return { data: res.data, error: null };
      },
    },
    // Legacy call sites are already rewired.
    // we expose these so those don't crash even though they no-op.
    realtime: realtimeWrapper,
  };
}
